import { twMerge } from "tailwind-merge";
import { parseISO, format, addHours } from "date-fns";
import { DATE_FORMATS } from "../../contanst/dateTimeConstants";
import { QuestionType } from "../enum";
import { useNavigate } from "react-router-dom";
// import { t as translate } from "i18next";

export function cn(...classes) {
  return twMerge(...classes);
}

export const formatDateTime = (
  dateTimeString,
  formatPattern = DATE_FORMATS.DEFAULT
) => {
  if (!dateTimeString) return "";

  try {
    const parsedDate = parseISO(dateTimeString);
    if (isNaN(parsedDate.getTime())) {
      return "Invalid date";
    }
    return format(parsedDate, formatPattern);
  } catch (error) {
    // console.error("Error formatting date:", error);
    return dateTimeString;
  }
};

export const calculateDaysLeft = (expireTime) => {
  if (!expireTime) return "Invalid date";

  const today = new Date();
  const targetDate = new Date(expireTime);

  const timeDiff = targetDate - today;

  if (timeDiff < 0) return "Expired";

  const daysLeft = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
  return `${daysLeft} days left`;
};

export const convertToUTC = (localTimeString) => {
  const localDate = new Date(localTimeString);
  const year = localDate.getUTCFullYear();
  const month = String(localDate.getUTCMonth() + 1).padStart(2, "0");
  const day = String(localDate.getUTCDate()).padStart(2, "0");
  const hours = String(localDate.getUTCHours()).padStart(2, "0");
  const minutes = String(localDate.getUTCMinutes()).padStart(2, "0");
  const seconds = String(localDate.getUTCSeconds()).padStart(2, "0");

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

export const getTimeRemaining = (expireTime) => {
  if (!expireTime)
    return {
      display: "Invalid date",
      ms: 0,
      minutes: 0,
      hours: 0,
      days: 0,
      months: 0,
      years: 0,
    };

  try {
    const expireTimeUTC = expireTime.endsWith("Z")
      ? expireTime
      : `${expireTime}Z`;
    const targetDate = new Date(expireTimeUTC);

    const now = new Date();

    const timeDiff = targetDate.getTime() - now.getTime();

    if (timeDiff < 0)
      return {
        display: "Expired",
        ms: 0,
        minutes: 0,
        hours: 0,
        days: 0,
        months: 0,
        years: 0,
      };
    const minutes = Math.floor(timeDiff / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);
    const years = Math.floor(months / 12);
    let display;
    if (minutes < 1) {
      display = "Expiring soon";
    } else if (hours < 1) {
      display = `${minutes} min${minutes !== 1 ? "s" : ""}`;
    } else if (days < 1) {
      display = `${hours} hour${hours !== 1 ? "s" : ""}`;
    } else if (days < 7) {
      const remainingHours = hours % 24;
      if (remainingHours > 0) {
        display = `${days} day${days !== 1 ? "s" : ""} ${remainingHours} hour${
          remainingHours !== 1 ? "s" : ""
        }`;
      } else {
        display = `${days} day${days !== 1 ? "s" : ""}`;
      }
    } else if (days < 30) {
      display = `${weeks} week${weeks !== 1 ? "s" : ""}`;
    } else {
      const remainingDays = days % 30;
      if (remainingDays > 0) {
        display = `${months} month${
          months !== 1 ? "s" : ""
        } ${remainingDays} day${remainingDays !== 1 ? "s" : ""}`;
      } else {
        display = `${months} month${months !== 1 ? "s" : ""}`;
      }
    }

    return {
      display,
      ms: timeDiff,
      minutes,
      hours,
      days,
      weeks,
      months,
      years,
    };
  } catch (error) {
    console.error("Error calculating time remaining:", error);
    return {
      display: "Error",
      ms: 0,
      minutes: 0,
      hours: 0,
      days: 0,
      months: 0,
      years: 0,
    };
  }
};

export const calculateDays = (expireTime) => {
  return getTimeRemaining(expireTime).display;
};

export const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return "";
  const formattedPhone =
    phoneNumber.length > 4
      ? `${phoneNumber.slice(0, 4)}-${phoneNumber.slice(4, 8)}`
      : phoneNumber;

  return formattedPhone;
};

export const isSelectedForRequiredQuest = (type, optionId, answerText) => {
  switch (type) {
    case QuestionType.CHECKBOX:
    case QuestionType.MCQ:
    case QuestionType.SLIDER:
    case QuestionType.YESNO:
      return optionId ? true : false;
    case QuestionType.SHORT:
    case QuestionType.LONG:
      return answerText ? true : false;
    default:
      return false;
  }
};

export const convertToSingaporeTime = (
  timestamp,
  formatPattern = DATE_FORMATS.DEFAULT
) => {
  if (!timestamp) return "";

  try {
    const parsedDate = parseISO(timestamp);

    if (isNaN(parsedDate.getTime())) {
      return "Invalid date";
    }

    const singaporeTime = addHours(parsedDate, 8);

    return format(singaporeTime, formatPattern);
  } catch (error) {
    console.error("Error converting to Singapore time:", error);
    return timestamp;
  }
};
export const renderWithBold = (text) => {
  const parts = text.split(/(\*\*[^*]+\*\*)|(\n)/g);
  return parts.map((part, idx) => {
    if (!part) return null;
    if (part === "\n") return <br key={idx} />;
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <strong key={idx} className="text-black font-semibold">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return <span key={idx}>{part}</span>;
  });
};

export const formatTime = (input) => {
  const date = new Date(input);

  const day = date.getDate();
  const month = date.toLocaleString("en-US", { month: "short" });
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${day} ${month} ${year} ${hours}:${minutes}`;
};

export const parseRallyUrl = (url) => {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    const searchParams = new URLSearchParams(urlObj.search);

    const pathSegments = pathname
      .split("/")
      .filter((segment) => segment !== "");

    const result = {
      questId: null,
      locationId: null,
      from: searchParams.get("from"),
      type: null,
      originalUrl: url,
    };

    if (pathSegments.includes("quest") && pathSegments.includes("details")) {
      const detailsIndex = pathSegments.indexOf("details");
      if (detailsIndex + 1 < pathSegments.length) {
        result.questId = pathSegments[detailsIndex + 1];
        result.type = "quest-details";
      }
    }

    if (pathSegments.includes("quest") && pathSegments.includes("location")) {
      const locationIndex = pathSegments.indexOf("location");
      if (locationIndex + 1 < pathSegments.length) {
        result.locationId = pathSegments[locationIndex + 1];
        result.type = "quest-location";
      }
    }

    searchParams.forEach((value, key) => {
      if (!result.hasOwnProperty(key)) {
        result[key] = value;
      }
    });

    return result;
  } catch (error) {
    console.error("Error parsing Rally URL:", error);
    return {
      questId: null,
      locationId: null,
      from: null,
      type: null,
      originalUrl: url,
      error: error.message,
    };
  }
};

export const isValidRallyUrl = (url) => {
  try {
    const urlObj = new URL(url);
    return (
      urlObj.hostname.includes("letsrally") || urlObj.hostname.includes("rally")
    );
  } catch {
    return false;
  }
};

export const extractIdFromUrl = (url, type) => {
  try {
    const parsed = parseRallyUrl(url);

    switch (type) {
      case "quest":
        return parsed.questId;
      case "location":
        return parsed.locationId;
      default:
        return null;
    }
  } catch {
    return null;
  }
};

// export const formatTimeAgo = (timestamp) => {
//   const date = new Date(timestamp);
//   const now = new Date();

//   const timeDiffSeconds = Math.floor((now - date) / 1000);

//   if (timeDiffSeconds < 60) {
//     return `${timeDiffSeconds}s ago`;
//   }

//   const timeDiffMinutes = Math.floor(timeDiffSeconds / 60);
//   if (timeDiffMinutes < 60) {
//     return `${timeDiffMinutes}m ago`;
//   }

//   const timeDiffHours = Math.floor(timeDiffMinutes / 60);
//   if (timeDiffHours < 24) {
//     return `${timeDiffHours}h ago`;
//   }

//   const timeDiffDays = Math.floor(timeDiffHours / 24);
//   if (timeDiffDays < 7) {
//     return `${timeDiffDays}d ago`;
//   }

//   const timeDiffWeeks = Math.floor(timeDiffDays / 7);
//   return `${timeDiffWeeks}w ago`;
// };
export const formatTimeAgo = (timestamp) => {
  const timestampWithTZ =
    timestamp.includes("Z") || timestamp.includes("+")
      ? timestamp
      : timestamp + "Z";

  const date = new Date(timestampWithTZ);
  const now = new Date();

  const timeDiffSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (timeDiffSeconds < 60) {
    return `${timeDiffSeconds}s ago`;
  }

  const timeDiffMinutes = Math.floor(timeDiffSeconds / 60);
  if (timeDiffMinutes < 60) {
    return `${timeDiffMinutes}m ago`;
  }

  const timeDiffHours = Math.floor(timeDiffMinutes / 60);
  if (timeDiffHours < 24) {
    return `${timeDiffHours}h ago`;
  }

  const timeDiffDays = Math.floor(timeDiffHours / 24);
  if (timeDiffDays < 7) {
    return `${timeDiffDays}d ago`;
  }

  const timeDiffWeeks = Math.floor(timeDiffDays / 7);
  return `${timeDiffWeeks}w ago`;
};

export const formatDate = (dateString) => {
  try {
    const utcDate = new Date(
      dateString.endsWith("Z") ? dateString : dateString + "Z"
    );

    if (isNaN(utcDate.getTime())) {
      return dateString;
    }

    const formatter = new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Asia/Singapore",
      hour12: false,
    });

    const result = formatter.format(utcDate);
    return result.replace(",", "");
  } catch (error) {
    return dateString;
  }
};
export const formatTimeSGT = (dateString) => {
  try {
    const utcDate = new Date(
      dateString.endsWith("Z") ? dateString : dateString + "Z"
    );

    if (isNaN(utcDate.getTime())) {
      return dateString;
    }

    const formatter = new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: "Asia/Singapore",
    });

    return formatter.format(utcDate).replace(",", "");
  } catch (error) {
    return dateString;
  }
};
export const formatDateSimple = (dateString) => {
  try {
    const date = new Date(dateString);

    if (isNaN(date.getTime())) {
      return dateString;
    }

    const formatter = new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    const result = formatter.format(date);
    return result.replace(",", "");
  } catch (error) {
    return dateString;
  }
};

export const convertLocalToUTC = (date) => {
  if (!(date instanceof Date) || isNaN(date)) {
    throw new Error("Đầu vào phải là một đối tượng Date hợp lệ");
  }

  return new Date(
    Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      date.getUTCHours(),
      date.getUTCMinutes(),
      date.getUTCSeconds(),
      date.getUTCMilliseconds()
    )
  );
};

export const convertToUTCString = (date) => {
  return convertToUTC(date).toISOString();
};

export const getMinutesDifference = (timeStr, date) => {
  const timeDate = new Date(timeStr);
  const utcDate = convertLocalToUTC(date);
  const diffMs = timeDate.getTime() - utcDate.getTime();
  return diffMs / (1000 * 60);
};

export const formatDateToFakeMicroUTC = (date) => {
  const pad = (n, width = 2) => n.toString().padStart(width, "0");

  const year = date.getUTCFullYear();
  const month = pad(date.getUTCMonth() + 1);
  const day = pad(date.getUTCDate());
  const hours = pad(date.getUTCHours());
  const minutes = pad(date.getUTCMinutes());
  const seconds = pad(date.getUTCSeconds());
  const milliseconds = pad(date.getUTCMilliseconds(), 3);

  const microseconds = `${milliseconds}000`;

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${microseconds}`;
};
export const convertUTCToLocalString = (utcStr) => {
  if (!utcStr || typeof utcStr !== "string") {
    throw new Error("Invalid UTC string");
  }

  const utcDate = new Date(utcStr + (utcStr.endsWith("Z") ? "" : "Z"));

  if (isNaN(utcDate.getTime())) {
    throw new Error("Invalid date format");
  }

  const localYear = utcDate.getFullYear();
  const localMonth = String(utcDate.getMonth() + 1).padStart(2, "0");
  const localDay = String(utcDate.getDate()).padStart(2, "0");
  const localHours = String(utcDate.getHours()).padStart(2, "0");
  const localMinutes = String(utcDate.getMinutes()).padStart(2, "0");
  const localSeconds = String(utcDate.getSeconds()).padStart(2, "0");
  const localMilliseconds = String(utcDate.getMilliseconds()).padStart(3, "0");

  return `${localYear}-${localMonth}-${localDay}T${localHours}:${localMinutes}:${localSeconds}.${localMilliseconds}`;
};

export const getLocationFromIP = async ({
  apiUrl = "https://ipinfo.io/json",
  timeout = 5000,
  fallbackLocation = {
    latitude: 1.3521,
    longitude: 103.8198,
    city: "Singapore",
    country: "Singapore",
  },
  includeLocationInfo = true,
} = {}) => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    const response = await fetch(apiUrl, {
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    // ipinfo.io trả về loc dạng "lat,lng"
    const [latitude, longitude] = data.loc.split(",");

    const result = {
      latitude: parseFloat(latitude),
      longitude: parseFloat(longitude),
      accuracy: 10000,
      source: "ip",
      error: null,
    };

    if (includeLocationInfo) {
      result.city = data.city || "Unknown";
      result.region = data.region || "Unknown";
      result.country = data.country || "Unknown";
      result.timezone = data.timezone || "Unknown";
      result.ip = data.ip || "Unknown";
    }

    return result;
  } catch (error) {
    console.error("Failed to get location from IP:", error.message);
    return {
      ...fallbackLocation,
      source: "default",
      error: error.message || "Failed to get IP location",
    };
  }
};

export const getSimpleIPLocation = async () => {
  const location = await getLocationFromIP({
    includeLocationInfo: false,
  });

  return {
    latitude: location.latitude,
    longitude: location.longitude,
    source: location.source,
    error: location.error,
  };
};

export const getIPLocationSingapore = async () => {
  return await getLocationFromIP({
    fallbackLocation: {
      latitude: 1.3521,
      longitude: 103.8198,
      city: "Singapore",
      country: "Singapore",
    },
  });
};

export const getIPLocationVietnam = async () => {
  return await getLocationFromIP({
    fallbackLocation: {
      latitude: 16.0544,
      longitude: 108.2022,
      city: "Da Nang",
      country: "Vietnam",
    },
  });
};

// export const getSmartLocation = async ({
//   gpsTimeout = 10000,
//   ipFallback = true,
//   fallbackLocation = {
//     latitude: 1.3521,
//     longitude: 103.8198,
//     city: "Singapore",
//     country: "Singapore",
//   },
//   desiredAccuracy = 100, // mét
// } = {}) => {
//   const tryGPS = () => {
//     return new Promise((resolve, reject) => {
//       if (!navigator.geolocation)
//         return reject(new Error("No geolocation support"));

//       navigator.geolocation.getCurrentPosition(
//         (pos) => {
//           const { latitude, longitude, accuracy } = pos.coords;
//           if (accuracy && accuracy <= desiredAccuracy) {
//             resolve({
//               latitude,
//               longitude,
//               accuracy,
//               source: "gps",
//               error: null,
//             });
//           } else {
//             reject(new Error(`GPS accuracy too low: ${accuracy}m`));
//           }
//         },
//         (err) => {
//           reject(err);
//         },
//         {
//           enableHighAccuracy: true,
//           timeout: gpsTimeout,
//           maximumAge: 0,
//         }
//       );
//     });
//   };

//   try {
//     const gpsLocation = await tryGPS();
//     return gpsLocation;
//   } catch (gpsError) {
//     if (ipFallback) {
//       try {
//         const ipLocation = await getIPLocationSingapore();
//         return {
//           ...ipLocation,
//           source: "ip",
//         };
//       } catch (ipError) {
//         console.error("IP fallback failed:", ipError.message);
//       }
//     }

//     return {
//       ...fallbackLocation,
//       source: "default",
//       error: "Unable to get location from GPS or IP",
//     };
//   }
// };

export const getSmartLocation = async ({
  gpsTimeout = 10000,
  ipFallback = true,
  fallbackLocation = {
    latitude: 1.3521,
    longitude: 103.8198,
  },
  desiredAccuracy = 100,
} = {}) => {
  const tryGPS = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        return reject(new Error("Trình duyệt không hỗ trợ GPS"));
      }

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude, longitude, accuracy } = pos.coords;

          if (accuracy && accuracy <= desiredAccuracy) {
            resolve({
              latitude,
              longitude,
              accuracy,
              source: "gps",
              error: null,
            });
          } else {
            reject(new Error(`GPS không đủ chính xác: ${accuracy}m`));
          }
        },
        (err) => reject(err),
        {
          enableHighAccuracy: true,
          timeout: gpsTimeout,
          maximumAge: 0,
        }
      );
    });
  };

  const getIPLocation = async () => {
    try {
      const response = await fetch("https://ipapi.co/json/");
      const data = await response.json();
      return {
        latitude: data.latitude || fallbackLocation.latitude,
        longitude: data.longitude || fallbackLocation.longitude,
        source: "ip",
        error: null,
      };
    } catch (error) {
      throw new Error("Không thể lấy location từ IP");
    }
  };

  try {
    const gpsLocation = await tryGPS();
    return gpsLocation;
  } catch (gpsError) {
    console.warn("GPS lỗi:", gpsError.message);

    if (ipFallback) {
      try {
        const ipLocation = await getIPLocation();
        return ipLocation;
      } catch (ipError) {
        console.warn("Lấy location từ IP thất bại:", ipError.message);
      }
    }

    return {
      ...fallbackLocation,
      source: "default",
      error: "Không thể lấy location từ GPS hoặc IP",
    };
  }
};

export const getJoinQuestCountdown = () => {
  const now = new Date();
  now.setHours(now.getHours() + 7);

  const targetDate = new Date(now);
  const dayOfWeek = targetDate.getDay();
  const daysUntilMonday = (8 - dayOfWeek) % 7;
  targetDate.setDate(targetDate.getDate() + daysUntilMonday);
  targetDate.setHours(0, 0, 0, 0);

  const diffMs = targetDate.getTime() - now.getTime();

  if (diffMs <= 0) {
    targetDate.setDate(targetDate.getDate() + 7);
  }

  const diffMsFinal = targetDate.getTime() - now.getTime();

  const diffSeconds = Math.floor(diffMsFinal / 1000);
  const days = Math.floor(diffSeconds / (3600 * 24));
  const hours = Math.floor((diffSeconds % (3600 * 24)) / 3600);
  const minutes = Math.floor((diffSeconds % 3600) / 60);
  const seconds = diffSeconds % 60;

  if (days >= 1) {
    return `Join quest in ${days} day${days > 1 ? "s" : ""}`;
  } else if (hours >= 1) {
    return `Join quest in ${hours} hour${hours > 1 ? "s" : ""}`;
  } else if (minutes >= 1) {
    return `Join quest in ${minutes} minute${minutes > 1 ? "s" : ""}`;
  } else {
    return `Join quest in ${String(hours).padStart(2, "0")}:${String(
      minutes
    ).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }
};

export const getDayCountdown = () => {
  const now = new Date();

  const targetDate = new Date(now);
  targetDate.setHours(23, 59, 59, 999);

  const diffMs = targetDate.getTime() - now.getTime();

  if (diffMs < 0) {
    targetDate.setDate(targetDate.getDate() + 1);
    targetDate.setHours(23, 59, 59, 999);
  }

  const diffMsFinal = targetDate.getTime() - now.getTime();

  const diffSeconds = Math.floor(diffMsFinal / 1000);
  const hours = Math.floor(diffSeconds / 3600);
  const minutes = Math.floor((diffSeconds % 3600) / 60);
  const seconds = diffSeconds % 60;

  return `Join quest in ${String(hours).padStart(2, "0")}:${String(
    minutes
  ).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

export const AnswerWithLink = ({ answer, searchTerm = "" }) => {
  const navigate = useNavigate();

  const highlightText = (text, searchTerm) => {
    if (!searchTerm) return text;

    const regex = new RegExp(`(${searchTerm})`, "gi");
    const parts = text.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <span key={index} className="bg-yellow-200 font-medium">
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  const handleLinkClick = (linkPath) => {
    navigate(linkPath);
  };

  if (typeof answer === "object" && answer.type === "withLink") {
    const { content, linkText, linkPath } = answer;

    const parts = content.split(`<link>${linkText}</link>`);

    return (
      <p className="text-[16px] text-[#A0A0A0] font-medium leading-5">
        {highlightText(parts[0], searchTerm)}
        <span
          onClick={() => handleLinkClick(linkPath)}
          className=" underline cursor-pointer"
        >
          {highlightText(linkText, searchTerm)}
        </span>
        {parts[1] && highlightText(parts[1], searchTerm)}
      </p>
    );
  }

  const textContent = typeof answer === "object" ? answer.content : answer;

  return (
    <p className="text-[16px] text-[#A0A0A0] font-medium leading-5">
      {highlightText(textContent, searchTerm)}
    </p>
  );
};
